import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaBars, FaTimes, FaSearch, FaWhatsapp, FaInstagram, FaFacebook, FaShoppingCart, FaUser, FaBox } from 'react-icons/fa';
import { toggleSidebar, toggleSearch, closeSidebar, toggleCart } from '../../store/slices/uiSlice';
import { selectCartItemCount } from '../../store/slices/cartSlice';
import { selectModuleEnabled } from '../../store/slices/tenantSlice';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sidebarOpen, searchOpen } = useSelector((state) => state.ui);
  const { items: categories } = useSelector((state) => state.categories);
  const settings = useSelector((state) => state.settings.data);
  const cartItemCount = useSelector(selectCartItemCount);
  const { isAuthenticated, user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Check module access
  const blogEnabled = useSelector(selectModuleEnabled('BLOGS'));
  const workshopsEnabled = useSelector(selectModuleEnabled('WORKSHOPS'));

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      dispatch(toggleSearch());
      setSearchQuery('');
    }
  };

  return (
    <nav className="sticky top-0 z-50" style={{ backgroundColor: 'var(--color-surface)' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition">
            {settings?.logo ? (
              <img src={settings.logo} alt={settings.companyName || 'Logo'} className="h-10 w-auto" />
            ) : (
              <div className="w-10 h-10 rounded flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: 'var(--color-secondary)' }}>
                S
              </div>
            )}
            <span className="font-semibold" style={{ color: 'var(--color-text)' }}>{settings?.companyName || 'ShopHub'}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:opacity-80 transition" style={{ color: 'var(--color-text)' }}>Home</Link>
            <Link to="/products" className="hover:opacity-80 transition" style={{ color: 'var(--color-text)' }}>Products</Link>
            <div className="relative group">
              <button className="transition" style={{ color: 'var(--color-text)' }}>Categories</button>
              <div className="absolute top-full left-0 mt-2 w-48 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300" style={{ backgroundColor: 'var(--color-surface)' }}>
                {categories.slice(0, 5).map((cat) => (
                  <Link
                    key={cat._id}
                    to={`/category/${cat._id}`}
                    className="block px-4 py-2 transition hover:opacity-80" style={{ color: 'var(--color-text)' }}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
            {blogEnabled && (
              <Link to="/blog" className="text-gray-700 hover:text-primary transition">Blog</Link>
            )}
            {workshopsEnabled && (
              <Link to="/workshops" className="text-gray-700 hover:text-primary transition">Workshops</Link>
            )}
            <Link to="/about" className="text-gray-700 hover:text-primary transition">About</Link>
            <Link to="/contact" className="text-gray-700 hover:text-primary transition">Contact</Link>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => dispatch(toggleSearch())}
              className="p-2 text-gray-700 hover:text-primary transition relative"
            >
              <FaSearch />
            </button>
            <button
              onClick={() => dispatch(toggleCart())}
              className="p-2 text-gray-700 hover:text-primary transition relative"
              title="Shopping Cart"
            >
              <FaShoppingCart />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemCount > 9 ? '9+' : cartItemCount}
                </span>
              )}
            </button>
            {settings?.socialMedia?.facebook && (
              <a
                href={settings.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                title="Follow us on Facebook"
              >
                <FaFacebook />
                <span>Facebook</span>
              </a>
            )}
            {settings?.socialMedia?.instagram && (
              <a
                href={settings.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded hover:from-purple-600 hover:to-pink-600 transition"
                title="Follow us on Instagram"
              >
                <FaInstagram />
                <span>Instagram</span>
              </a>
            )}
            {settings?.whatsappLink && (
              <a
                href={settings.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded hover:bg-primary transition"
                title="Contact us on WhatsApp"
              >
                <FaWhatsapp />
                <span>Contact</span>
              </a>
            )}
            
            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 text-gray-700 hover:text-primary transition"
                >
                  <FaUser />
                  <span>{user?.profile?.fullName || user?.profile?.firstName || user?.username || 'Account'}</span>
                </button>
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-50">
                      <Link
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-gray-700 hover:bg-primary hover:text-white transition"
                      >
                        <FaUser className="inline mr-2" /> My Profile
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-gray-700 hover:bg-primary hover:text-white transition"
                      >
                        <FaBox className="inline mr-2" /> My Orders
                      </Link>
                      <hr className="my-2" />
                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                          navigate('/');
                        }}
                        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-red-500 hover:text-white transition"
                      >
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition"
              >
                <FaUser />
                <span>Login</span>
              </Link>
            )}
            
            <button
              onClick={() => dispatch(toggleSidebar())}
              className="md:hidden p-2 text-gray-700"
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="py-4 border-t">
            <form onSubmit={handleSearch} className="flex space-x-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="flex-1 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              <button
                type="submit"
                className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark transition"
              >
                Search
              </button>
            </form>
          </div>
        )}

        {/* Mobile Sidebar */}
        {sidebarOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-2">
              <Link 
                to="/" 
                onClick={() => dispatch(closeSidebar())}
                className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
              >
                Home
              </Link>
              <Link 
                to="/products" 
                onClick={() => dispatch(closeSidebar())}
                className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
              >
                Products
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat._id}
                  to={`/category/${cat._id}`}
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
                >
                  {cat.name}
                </Link>
              ))}
              {blogEnabled && (
                <Link 
                  to="/blog" 
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
                >
                  Blog
                </Link>
              )}
              {workshopsEnabled && (
                <Link 
                  to="/workshops" 
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
                >
                  Workshops
                </Link>
              )}
              <Link 
                to="/about" 
                onClick={() => dispatch(closeSidebar())}
                className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
              >
                About
              </Link>
              <Link 
                to="/contact" 
                onClick={() => dispatch(closeSidebar())}
                className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition"
              >
                Contact
              </Link>
              {settings?.socialMedia?.facebook && (
                <a
                  href={settings.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-blue-600 hover:text-white rounded transition flex items-center space-x-2"
                >
                  <FaFacebook />
                  <span>Facebook</span>
                </a>
              )}
              {settings?.socialMedia?.instagram && (
                <a
                  href={settings.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white rounded transition flex items-center space-x-2"
                >
                  <FaInstagram />
                  <span>Instagram</span>
                </a>
              )}
              {settings?.whatsappLink && (
                <a
                  href={settings.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-secondary hover:text-white rounded transition flex items-center space-x-2"
                >
                  <FaWhatsapp />
                  <span>Contact</span>
                </a>
              )}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => dispatch(closeSidebar())}
                    className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition flex items-center space-x-2"
                  >
                    <FaUser />
                    <span>My Profile</span>
                  </Link>
                  <Link
                    to="/orders"
                    onClick={() => dispatch(closeSidebar())}
                    className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition flex items-center space-x-2"
                  >
                    <FaBox />
                    <span>My Orders</span>
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      dispatch(closeSidebar());
                      navigate('/');
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-red-500 hover:text-white rounded transition"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => dispatch(closeSidebar())}
                  className="px-4 py-2 text-gray-700 hover:bg-primary hover:text-white rounded transition flex items-center space-x-2"
                >
                  <FaUser />
                  <span>Login</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

